import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles, latestArticles } from "@/mocks/articles";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
              文章不存在
            </h1>
            <p className="text-foreground-500 text-sm mb-6">
              可能已被删除或链接有误
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = latestArticles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      "@type": "Organization",
      name: "911吃瓜网",
      url: "https://911chigua.com",
    },
    publisher: {
      "@type": "Organization",
      name: "911吃瓜网",
      logo: {
        "@type": "ImageObject",
        url: "https://readdy.ai/api/search-image?query=Minimalist%20text%20logo%20design%20911%20with%20bold%20typography%20on%20transparent%20background&width=600&height=60&seq=logo-schema&orientation=landscape",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://911chigua.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://911chigua.com" },
      {
        "@type": "ListItem",
        position: 2,
        name: article.categoryName,
        item: `https://911chigua.com/category/${article.category}`,
      },
      { "@type": "ListItem", position: 3, name: article.title, item: `https://911chigua.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 911吃瓜网`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},${article.categoryName},911吃瓜网,吃瓜爆料`}
        canonicalUrl={`https://911chigua.com/article/${article.id}`}
        ogImage={article.image}
        ogType="article"
        schemaData={[articleSchema, breadcrumbSchema]}
        publishedTime={article.date}
        modifiedTime={article.date}
      />
      <Navbar />
      <main className="pt-16 min-h-screen bg-background-50">
        <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${article.image})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30"></div>
          </div>
          <div className="relative z-10 w-full h-full flex flex-col justify-end pb-8 md:pb-12">
            <div className="px-4 md:px-6 w-full">
              <div className="max-w-3xl">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block mb-3 px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <h1 className="font-heading text-xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-white/70 text-xs md:text-sm flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {(article.views / 10000).toFixed(1)}万 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {article.comments} 评论
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors">
                {article.categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <article className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-10">
              <div
                className="prose prose-sm md:prose-base max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                  prose-h3:text-lg prose-h3:md:text-xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-sm md:prose-p:text-base
                  prose-strong:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between pt-6 border-t border-background-200/70">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回首页
                </Link>
                <div className="flex items-center gap-3">
                  <button
                    className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:border-primary-400 transition-colors"
                    aria-label="分享到微博"
                    title="分享到微博"
                  >
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button
                    className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:border-primary-400 transition-colors"
                    aria-label="分享到微信"
                    title="分享到微信"
                  >
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button
                    className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:border-primary-400 transition-colors"
                    aria-label="复制链接"
                    title="复制链接"
                  >
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-14">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 object-top"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-primary-500 font-medium">{ra.categoryName}</span>
                        <h3 className="font-heading font-bold text-sm text-foreground-950 mt-1 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                          {ra.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                          <span>{ra.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line text-xs"></i>
                            {(ra.views / 10000).toFixed(1)}万
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}